// ============================================================================
// STUDY SET — LifeOS School.dc.html, option 6c, the 400px right column.
//
// Three blocks, and the thing they have in common is the point of the screen: EVERY ONE OF THEM
// WAS WRITTEN BY A MODEL, AND EVERY ONE OF THEM SAYS SO.
//
//   AI summary          'generated from transcript · Sep 10'
//   Flashcards          'generated from this lecture · 12:31'   (per card, on both faces)
//   Practice questions  'generated from transcript · Sep 10'
//
// The flashcard label sits on the question face as well as the answer face, which the mockup
// does not show — the mockup photographs a flipped card. A provenance label that only appears
// after an interaction is a provenance label that is missing at rest, and rest is when the
// reader decides how much to believe.
//
// Each block goes through <Provenanced>, so an artifact that cannot name its model, its source
// and its time does not render at all.
//
// THE FLASHCARD HAS ONE BUTTON, AND IT IS CALLED `next` (owner decision, 2026-09-20). It used to
// have two, `again` and `good`, in red and green — the grading pair every spaced-repetition app
// draws. They ran the identical handler: advance one card, unflip. Nothing stores a review, so
// there was nothing for a grade to be written to. Two buttons that differ only in colour, while
// claiming to record how well you knew something, is the screen faking knowledge — the one thing
// this product does not do. One button that says what it does, and a title that says what it does
// NOT do, is the honest shape. (The `--color-danger-lift` / `--color-success-lift` tokens stay in
// index.css; the mobile recorder still uses the first, and a token is cheap.)
//
// AND THE CARD GROWS WITH ITS TEXT. The stack wrapper was `h-[104px]` with the live card absolutely
// positioned inside it, so any answer longer than about two lines ran straight out of the dark slab
// and over the section below (owner report, 2026-09-20 — the flipped face of most cards in this
// lecture). The live card is now in normal flow and sets the height; the two dead layers behind it
// are still absolute, so they stretch to whatever the card turns out to be and the stack still
// reads as a stack. `min-h` keeps the mockup's 104px as a floor, never as a ceiling.
// ============================================================================

import { useId, useRef, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Flashcard, LectureSummary, PracticeQuestion } from '@app/shared'

import { SkeletonRows } from '../Card'
import { useGenerateStudySet } from '../../lib/data'
import { Cross, Flip, SpeechPlain, Tick } from './icons'
import { Unavailable } from './Chrome'
import { Eyebrow, Provenanced, ProvenanceLabel } from './Provenance'

const CARD = 'flex flex-col gap-[9px] rounded-context border border-line bg-surface px-4 py-3 shadow-card'

// ── AI summary ──────────────────────────────────────────────────────────────

export function SummaryCard({
  summary,
  lectureId,
  isPending,
  error,
}: {
  summary: LectureSummary | null | undefined
  /** Which lecture to read. Generation lives on this card because it makes all three at once. */
  lectureId: string | undefined
  isPending: boolean
  error: unknown
}) {
  // ONE BUTTON FOR THREE PANELS, because one model call makes all three artifacts from one
  // transcript and they share one provenance (apps/worker/src/routes/study.ts). A per-panel
  // button would suggest three independent generations that can disagree about which model said
  // what, which is exactly the thing that endpoint is shaped to prevent.
  const generate = useGenerateStudySet()

  return (
    <section className={`${CARD} flex-none`}>
      {isPending && <SkeletonRows rows={3} />}
      <Unavailable error={error} />
      <Provenanced
        artifact={summary}
        fallback={
          !isPending && !error ? (
            <div className="flex flex-col items-start gap-[9px]">
              <p className="text-[12px] leading-[1.45] text-microcopy text-pretty">
                {/* NOT "yet, it is coming". Nothing generates a study set on a schedule — a
                    transcript landing does not trigger one — so the honest sentence is that
                    nobody has asked for it, and the button is how you ask. */}
                No study material has been generated for this lecture. Nothing makes it
                automatically; the transcript below is the whole of what the system knows until
                you ask for one.
              </p>
              <button
                type="button"
                disabled={!lectureId || generate.isPending}
                onClick={() => lectureId && generate.mutate(lectureId)}
                className="rounded-button bg-ink px-3 py-[7px] text-left text-[11.5px] font-semibold leading-none text-surface disabled:cursor-not-allowed disabled:bg-fill disabled:text-microcopy"
              >
                {/* Short enough for the 400px column at the owner's 130% UI scale. What it makes
                    is named in the sentence above, not crammed into the button. */}
                {generate.isPending ? 'Reading the transcript…' : 'Generate study material'}
              </button>
              {generate.error && (
                // The Worker's own sentence. "No transcript", "the model API did not answer" and
                // "this deployment has no ANTHROPIC_API_KEY" are three different next moves.
                <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
                  {generate.error instanceof Error
                    ? generate.error.message
                    : 'The study material could not be generated.'}
                </p>
              )}
              {generate.data?.note && (
                // The degradation sentence, never swallowed: a tier that could not be served says
                // so, and the provenance names the model that actually answered.
                <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
                  {generate.data.note}
                </p>
              )}
            </div>
          ) : null
        }
      >
        {(artifact) => (
          <>
            <div className="flex items-center gap-2">
              <Eyebrow>AI SUMMARY</Eyebrow>
              <ProvenanceLabel what="generated from transcript" artifact={artifact} />
            </div>
            {artifact.bullets.map((bullet) => (
              <div key={bullet} className="flex gap-2">
                <span className="mt-[6px] size-1 flex-none rounded-full bg-school" aria-hidden />
                <p className="text-[12px] leading-[1.5] text-ink-2 text-pretty">{bullet}</p>
              </div>
            ))}
            {artifact.keyConcepts.length > 0 && (
              <div className="flex flex-wrap items-center gap-[7px] pt-[2px]">
                {artifact.keyConcepts.map((concept) => (
                  <span
                    key={concept}
                    className="rounded-pill bg-fill-2 px-[9px] py-1 text-[10.5px] font-medium leading-none text-ink-2"
                  >
                    {concept}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </Provenanced>
    </section>
  )
}

// ── Flashcards ──────────────────────────────────────────────────────────────

export function FlashcardsCard({
  flashcards,
  isPending,
  error,
}: {
  flashcards: Flashcard[] | undefined
  isPending: boolean
  error: unknown
}) {
  const cards = flashcards ?? []
  const due = cards.filter((c) => c.dueForReview).length
  // Open on the first card that is actually due, because that is the one a review would start on.
  const startIndex = Math.max(0, cards.findIndex((c) => c.dueForReview))
  const [index, setIndex] = useState<number | null>(null)
  const [flipped, setFlipped] = useState(false)
  const position = index ?? startIndex
  const card = cards[position]

  return (
    <section className={`${CARD} flex-none`} id="flashcards">
      <header className="flex items-center gap-[9px]">
        <h2 className="text-[13px] font-semibold leading-none">Flashcards</h2>
        {flashcards && (
          <span className="text-[11px] font-normal leading-none text-microcopy">
            {cards.length} {cards.length === 1 ? 'card' : 'cards'}
            {due > 0 && ` · ${due} due for review`}
          </span>
        )}
        <button
          type="button"
          disabled
          title="A full review session — scheduling, intervals, what you got wrong last time — has not been built. `dueForReview` is DERIVED (a card never reviewed is due), so there is nothing yet that could remember an answer."
          className="ml-auto cursor-not-allowed text-[11px] font-medium leading-none text-success-dot"
        >
          Review
        </button>
      </header>

      {isPending && <SkeletonRows rows={3} />}
      <Unavailable error={error} />
      {flashcards && cards.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy text-pretty">
          No flashcards have been generated for this lecture.
        </p>
      )}

      <Provenanced artifact={card}>
        {(current) => (
          <div className="relative min-h-[104px] pb-2 pl-[6px] pr-[14px]">
            {/* The live card sits in the wrapper's PADDING BOX — `pb-2 pl-[6px] pr-[14px]`
                reproduces exactly the insets the old `absolute bottom-2 left-[6px] right-[14px]
                top-0` gave it — so the wrapper's height is the card's height plus the 8px the
                bottom layer peeks out by. Padding does not move the absolute layers below: their
                containing block is the wrapper's padding-box EDGE, which is unchanged.

                Two dead layers behind the live card — the stack is what says "there are more of
                these" without printing a number twice. Each is `inset`-stretched, so it tracks
                whatever height the card above it ends up at. */}
            <span
              className="absolute inset-x-[6px] bottom-0 top-2 rounded-panel border border-line-3 bg-fill-2"
              aria-hidden
            />
            <span
              className="absolute inset-x-[10px] inset-y-1 rounded-panel border border-line-3 bg-surface-2"
              aria-hidden
            />

            {/* `relative` only so it paints over the two absolute layers; `min-h-[96px]` keeps a
                one-line card at the mockup's size and gives `mt-auto` below its free space. */}
            <div className="relative flex min-h-[96px] flex-col gap-2 rounded-panel bg-slab px-4 py-[14px] shadow-flashcard">
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-semibold uppercase leading-none tracking-[0.08em] text-ink-3">
                  {flipped ? 'ANSWER' : 'QUESTION'} · CARD {position + 1} OF {cards.length}
                </span>
                <button
                  type="button"
                  onClick={() => setFlipped((f) => !f)}
                  className="ml-auto flex items-center gap-[5px] text-[9.5px] font-medium leading-none text-ink-3 hover:text-on-dark"
                >
                  <Flip size={10} />
                  flip
                </button>
              </div>

              <p className="text-[12px] font-normal leading-[1.45] text-on-dark text-pretty break-words">
                {flipped ? current.answer : current.prompt}
              </p>

              <div className="mt-auto flex items-center gap-2">
                {/* The label lives on BOTH faces. See the file header. */}
                <ProvenanceLabel
                  what="generated from this lecture"
                  artifact={current}
                  sourceMs={current.sourceMs}
                  className="text-[9.5px] font-medium leading-[1.4] text-ink-3"
                />
                {/* ONE button. See the file header for why there are no longer two. */}
                <button
                  type="button"
                  onClick={() => {
                    setIndex((position + 1) % cards.length)
                    setFlipped(false)
                  }}
                  title="Moves to the next card, and that is all it does. Nothing is scheduled or remembered: no review history is stored anywhere, so this button cannot claim to have taught you anything."
                  className="ml-auto flex-none rounded-pill bg-ink-raised px-[9px] py-1 text-[10.5px] font-semibold leading-none text-on-dark"
                >
                  next
                </button>
              </div>
            </div>
          </div>
        )}
      </Provenanced>
    </section>
  )
}

// ── Practice questions ──────────────────────────────────────────────────────

export function PracticeCard({
  questions,
  isPending,
  error,
  lectureLabel,
  courseCode,
  courseId,
}: {
  questions: PracticeQuestion[] | undefined
  isPending: boolean
  error: unknown
  /** 'Lecture 5' — what the ask bar puts in front of the question it sends. */
  lectureLabel: string
  /** 'COGSCI 175' — what the ask bar's scope chip names, because that is the scope it gets. */
  courseCode: string
  /** The row id behind `course:<id>`, the only parameterised chat scope there is. */
  courseId: string
}) {
  const rows = questions ?? []
  const answered = rows.filter((q) => q.answered).length
  const lead = rows.find((q) => q.answered) ?? rows[0]
  const next = rows.find((q) => !q.answered && q.id !== lead?.id)
  const [revealed, setRevealed] = useState(false)

  return (
    <section className={`${CARD} flex-none`} id="practice">
      <header className="flex flex-none items-center gap-[9px]">
        <h2 className="text-[13px] font-semibold leading-none">Practice questions</h2>
        {questions && (
          <span className="text-[11px] font-normal leading-none text-microcopy">
            {rows.length} · {answered} answered
          </span>
        )}
      </header>

      {isPending && <SkeletonRows rows={3} />}
      <Unavailable error={error} />
      {questions && rows.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy text-pretty">
          No practice questions have been generated for this lecture.
        </p>
      )}

      <Provenanced artifact={lead}>
        {(current) => (
          <>
            {/* The block's own provenance line. The mockup does not draw one here; every other
                machine-written block on this screen has one, and a question that looks like the
                professor set it is exactly the confusion the label prevents. */}
            <div className="flex flex-none items-center gap-2">
              <Eyebrow>AI PRACTICE</Eyebrow>
              <ProvenanceLabel what="generated from transcript" artifact={current} />
            </div>

            <div className="flex flex-col gap-[9px] rounded-row border border-fill bg-surface-2 px-[13px] py-3">
              <p className="text-[12.5px] font-semibold leading-[1.45] text-pretty">
                {current.question}
              </p>
              <p className="rounded-button border-l-[3px] border-live-dot bg-surface px-[11px] py-[9px] text-[12px] leading-[1.5] text-ink-2 text-pretty">
                {current.answer}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled
                  title="Recording what you got right needs somewhere to write it, and the study set stores no per-question progress. `answered` is derived: never worked, so false."
                  className="flex cursor-not-allowed items-center gap-[6px] rounded-button bg-success-fill px-3 py-[7px] text-[11.5px] font-semibold leading-none text-success"
                >
                  <Tick size={12} />
                  I got it
                </button>
                <button
                  type="button"
                  disabled
                  title="Recording what you missed needs somewhere to write it, and the study set stores no per-question progress. `answered` is derived: never worked, so false."
                  className="flex cursor-not-allowed items-center gap-[6px] rounded-button bg-danger-fill px-3 py-[7px] text-[11.5px] font-semibold leading-none text-danger-2"
                >
                  <Cross size={12} />
                  Missed it
                </button>
                <span className="ml-auto text-[11px] font-medium leading-none text-muted">
                  {answered} of {rows.length} answered
                </span>
              </div>
            </div>
          </>
        )}
      </Provenanced>

      {next && (
        <div className="flex flex-none items-center gap-[10px] rounded-row border border-fill bg-surface px-[13px] py-[11px]">
          <p className="min-w-0 flex-1 text-[12.5px] font-medium leading-[1.35] text-ink-2 text-pretty">
            {revealed ? next.answer : next.question}
          </p>
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            className="flex-none whitespace-nowrap text-[11px] font-semibold leading-none text-success-dot hover:underline"
          >
            {revealed ? 'Hide' : 'Reveal'}
          </button>
        </div>
      )}

      <AskAboutLecture lectureLabel={lectureLabel} courseCode={courseCode} courseId={courseId} />
    </section>
  )
}

/**
 * THE ASK BAR — the mockup's dark strip at the foot of the practice card, and since this PR it
 * actually asks. Type a question, press Enter (or Ask), and it opens a new thread on /chat
 * with the question already sent.
 *
 * IT SAYS "COGSCI 175" BECAUSE THAT IS THE SCOPE IT GETS. There is no per-lecture chat scope —
 * `ChatScopeId` is the five fixed scopes plus `course:<id>` (apps/web/src/lib/data/types.ts), and
 * the Worker resolves nothing narrower (apps/worker/src/lib/chat.ts → `resolveScope`). The chip
 * used to read "scoped to Lecture 5", which was the screen claiming a scope the engine has never
 * had. So the chip names the course, and the LECTURE is carried in the question's own text —
 * where it is both true and useful, since the engine's `get_transcript` tool takes a course and a
 * lecture number and can then go and read exactly this recording.
 *
 * MOTION: `.mo-ink`, not `.mo-field`. The text-entry mechanic paints `--color-field-hover`, a
 * near-white literal, and this bar is the one control in the app sitting on the dark `slab` — that
 * fill would invert it under the pointer. `.mo-ink` is the same left-to-right arrival with a
 * per-instance tint, so it takes the lecture page's own hue over the dark ground instead. Focus is
 * left canonical: the input carries no `outline-none`, so it gets the app-wide 2px ring.
 */
function AskAboutLecture({
  lectureLabel,
  courseCode,
  courseId,
}: {
  lectureLabel: string
  courseCode: string
  courseId: string
}) {
  const navigate = useNavigate()
  const inputId = useId()
  const input = useRef<HTMLInputElement>(null)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const question = input.current?.value.trim()
        if (!question) return
        navigate({
          pathname: '/chat',
          search: new URLSearchParams({
            thread: 'new',
            scope: `course:${courseId}`,
            // The lecture, named in the owner's own question rather than in a scope that does not
            // exist. `${lectureLabel}` is 'Lecture 5' or the recording's date — whatever this
            // screen calls it out loud, so the thread and this page agree about what was asked.
            ask: `About ${courseCode} ${lectureLabel}: ${question}`,
            send: '1',
          }).toString(),
        })
      }}
      style={{ '--mo-rule': 'var(--color-slab-school)' } as CSSProperties}
      className="mo-ink mt-auto flex flex-none items-center gap-[10px] rounded-row bg-slab px-[13px] py-[11px]"
    >
      <SpeechPlain size={15} strokeWidth={1.8} className="flex-none text-slab-school" />
      <label htmlFor={inputId} className="sr-only">
        Ask about {lectureLabel}
      </label>
      <input
        id={inputId}
        ref={input}
        type="text"
        autoComplete="off"
        placeholder="Ask about this lecture…"
        className="min-w-0 flex-1 bg-transparent text-[12.5px] font-normal leading-none text-on-dark placeholder:text-muted"
      />
      <span
        className="flex-none rounded-chip bg-ink-raised px-[10px] py-[5px] text-[10px] font-semibold leading-[1.4] text-muted"
        title={`Chat scopes are whole courses — there is no per-lecture scope. The question is sent with “${lectureLabel}” in it, so the answer can go to this recording.`}
      >
        scoped to {courseCode}
      </span>
      <button
        type="submit"
        className="mo-press flex-none rounded-chip px-[6px] py-[5px] text-[10px] font-semibold leading-[1.4] text-slab-school transition-colors duration-[var(--dur-hover)] ease-[var(--ease-ink)] hover:text-on-dark"
      >
        Ask
        <span className="sr-only"> — opens a chat scoped to {courseCode}</span>
      </button>
    </form>
  )
}
